import { useState, useMemo } from 'react';
import {
  MenuBook as BooksIcon,
  PeopleAlt as HRMSIcon,
  Leaderboard as LMSIcon,
  GavelRounded as PactIcon,
} from '@mui/icons-material';
import useAuth from './useAuth';
import apiClient from '../api/apiClient';

// Single source of truth for the KoorAI ONE suite's launchable modules.
// Consumed by both the sidebar "Connected Apps" list and the header suite switcher
// so labels, colors, and launch behavior never drift between the two.
const MODULE_DEFS = [
  {
    key: 'books',
    label: 'Books',
    description: 'Accounting & invoicing',
    icon: BooksIcon,
    ssoEndpoint: '/api/auth/niraibooks-sso',
    isEnabled: (user) => !!user?.niraiBooks?.enabled,
    color: '#6ee7b7',
    gradient: 'linear-gradient(135deg, rgba(37,99,235,0.15) 0%, rgba(16,185,129,0.10) 100%)',
    hoverGradient: 'linear-gradient(135deg, rgba(37,99,235,0.25) 0%, rgba(16,185,129,0.18) 100%)',
    border: 'rgba(37,99,235,0.2)',
    hoverBorder: 'rgba(37,99,235,0.35)',
    hoverColor: '#a7f3d0',
  },
  {
    key: 'hrms',
    label: 'HRMS',
    description: 'HR, payroll & attendance',
    icon: HRMSIcon,
    ssoEndpoint: '/api/auth/koorhrms-sso',
    isEnabled: (user) => !!user?.koorHRMS?.enabled,
    color: '#c4b5fd',
    gradient: 'linear-gradient(135deg, rgba(124,58,237,0.15) 0%, rgba(167,139,250,0.10) 100%)',
    hoverGradient: 'linear-gradient(135deg, rgba(124,58,237,0.25) 0%, rgba(167,139,250,0.18) 100%)',
    border: 'rgba(124,58,237,0.2)',
    hoverBorder: 'rgba(124,58,237,0.35)',
    hoverColor: '#ddd6fe',
  },
  {
    key: 'lms',
    label: 'LMS',
    description: 'Lead management',
    icon: LMSIcon,
    ssoEndpoint: '/api/auth/koorlms-sso',
    isEnabled: (user) => !!user?.koorLMS?.enabled,
    color: '#93c5fd',
    gradient: 'linear-gradient(135deg, rgba(59,130,246,0.15) 0%, rgba(37,99,235,0.10) 100%)',
    hoverGradient: 'linear-gradient(135deg, rgba(59,130,246,0.25) 0%, rgba(37,99,235,0.18) 100%)',
    border: 'rgba(59,130,246,0.2)',
    hoverBorder: 'rgba(59,130,246,0.35)',
    hoverColor: '#bfdbfe',
  },
  {
    key: 'pact',
    label: 'Pact',
    description: 'Contracts & e-sign',
    icon: PactIcon,
    ssoEndpoint: '/api/auth/koorpact-sso',
    isEnabled: (user) => !!user?.koorPact?.enabled,
    color: '#d8b4fe',
    gradient: 'linear-gradient(135deg, rgba(168,85,247,0.15) 0%, rgba(236,72,153,0.10) 100%)',
    hoverGradient: 'linear-gradient(135deg, rgba(168,85,247,0.25) 0%, rgba(236,72,153,0.18) 100%)',
    border: 'rgba(168,85,247,0.2)',
    hoverBorder: 'rgba(168,85,247,0.35)',
    hoverColor: '#e9d5ff',
  },
];

export default function useConnectedApps() {
  const { user, isSuperAdmin } = useAuth();
  const [loadingKey, setLoadingKey] = useState(null);

  const launch = async (mod) => {
    if (loadingKey) return;
    setLoadingKey(mod.key);
    try {
      const res = await apiClient.get(mod.ssoEndpoint);
      if (res.data?.success && res.data?.url) {
        window.open(res.data.url, '_blank', 'noopener,noreferrer');
      } else {
        alert(res.data?.message || `${mod.label} SSO not configured yet.`);
      }
    } catch (err) {
      alert(err.response?.data?.message || `Failed to open ${mod.label}. SSO may not be configured yet.`);
    } finally {
      setLoadingKey(null);
    }
  };

  const apps = useMemo(() => MODULE_DEFS.map((mod) => ({
    ...mod,
    enabled: isSuperAdmin || mod.isEnabled(user),
    loading: loadingKey === mod.key,
    onClick: () => launch(mod),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [user, isSuperAdmin, loadingKey]);

  const visibleApps = apps.filter((a) => a.enabled);

  return { apps, visibleApps };
}
